export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

export interface ApiOptions<TBody = unknown> {
  method?: HttpMethod
  signal?: AbortSignal
  body?: TBody
  headers?: Record<string, string>
}

export class HttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
    this.name = 'HttpError'
  }
}

function baseUrl() {
  const fromEnv = import.meta.env.VITE_ADMIN_API_BASE
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv.replace(/\/$/, '')
  // Same-origin + API under /api
  return '/api'
}

function shouldRedirectAuth(status: number) {
  return status === 401 || status === 403
}

function handleAuthRedirect(status: number) {
  if (shouldRedirectAuth(status)) {
    const loginPath = '/login'
    if (!location.pathname.startsWith(loginPath)) {
      location.assign(loginPath)
    }
  }
}

export async function apiFetch<TRes = unknown, TBody = unknown>(
  path: string,
  options: ApiOptions<TBody> = {},
): Promise<TRes> {
  const url = `${baseUrl()}${path}`
  const { method = 'GET', body, signal, headers = {} } = options

  const res = await fetch(url, {
    method,
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body != null ? JSON.stringify(body) : undefined,
    credentials: 'include',
  })

  if (!res.ok) {
    handleAuthRedirect(res.status)
    const text = await res.text().catch(() => '')
    throw new HttpError(res.status, `Request failed ${res.status}: ${text || res.statusText}`)
  }
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return (await res.json()) as TRes
  }
  return (await res.text()) as unknown as TRes
}

export const get = <T>(path: string, opts?: ApiOptions) => apiFetch<T>(path, { ...opts, method: 'GET' })
export const post = <TRes, TBody>(path: string, body: TBody, opts?: ApiOptions<TBody>) =>
  apiFetch<TRes, TBody>(path, { ...opts, method: 'POST', body })
export const put = <TRes, TBody>(path: string, body: TBody, opts?: ApiOptions<TBody>) =>
  apiFetch<TRes, TBody>(path, { ...opts, method: 'PUT', body })
export const del = <T>(path: string, opts?: ApiOptions) => apiFetch<T>(path, { ...opts, method: 'DELETE' })
export const patch = <TRes, TBody>(path: string, body: TBody, opts?: ApiOptions<TBody>) =>
  apiFetch<TRes, TBody>(path, { ...opts, method: 'PATCH', body })
